import React, { useEffect, useId, useState } from "react";
import Tippy from "@tippyjs/react";
import getSymbolFromCurrency from "currency-symbol-map";
import Tooltip from "components/Tooltip";
import {
  getPlayerFinancialSummary,
  PlayerFinancialSummary,
} from "api/playerFinancialSummary";
import styles from "./PlayerProfitability.module.scss";

export function formatSummaryAmount(value: string, currency: string) {
  const symbol = getSymbolFromCurrency(currency) ?? `${currency} `;
  return value.startsWith("-")
    ? `-${symbol}${value.slice(1)}`
    : `${symbol}${value}`;
}

const labels = {
  profitable: "Profitable",
  unprofitable: "Unprofitable",
  neutral: "Neutral",
};

export default function PlayerProfitability({ userId }: { userId: number }) {
  const [summary, setSummary] = useState<PlayerFinancialSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const id = useId();

  useEffect(() => {
    const controller = new AbortController();
    setSummary(null);
    setFailed(false);
    setOpen(false);
    if (!Number.isSafeInteger(userId) || userId <= 0) return;
    getPlayerFinancialSummary(userId, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSummary(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [userId, attempt]);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", close, true);
    return () => document.removeEventListener("keydown", close, true);
  }, [open]);

  if (!Number.isSafeInteger(userId) || userId <= 0) return null;
  const status = summary?.profitability.status;
  const amount = (value: string, currency = summary!.balances.currency) =>
    formatSummaryAmount(value, currency);
  const realGgrReady =
    summary?.profitability.realGgrAvailable === true &&
    typeof summary.profitability.realGgr === "string" &&
    summary.profitability.realGgrCurrency.length > 0;
  const rows = summary
    ? [
        ["Deposits", summary.statistics.totalDeposits],
        ["Withdrawals", summary.statistics.totalWithdrawals],
        ["Real", summary.balances.real],
        ["Bonus", summary.balances.bonus],
        ["Hold", summary.balances.hold],
      ]
    : [];

  return (
    <div className={styles.wrapper}>
      {failed ? (
        <div className={styles.message} role="status">
          Unavailable{" "}
          <button type="button" onClick={() => setAttempt(attempt + 1)}>
            Retry
          </button>
        </div>
      ) : !summary ? (
        <div className={styles.message} role="status">
          Loading…
        </div>
      ) : (
        <div className={styles.actions}>
          <Tippy
            className={styles.tooltip}
            arrow={false}
            visible={open}
            onClickOutside={() => setOpen(false)}
            interactive
            appendTo={document.body}
            placement="bottom-end"
            zIndex={999999}
            role="dialog"
            aria={{ content: null, expanded: false }}
            content={
              <div
                id={id}
                className={`${styles.popover} ${styles[status!]}`}
                aria-label="Current profitability snapshot"
              >
                <div className={styles.heading}>
                  Current profitability snapshot
                </div>
                <dl>
                  {rows.map(([label, value]) => (
                    <div className={styles.row} key={label}>
                      <dt>{label}</dt>
                      <dd>{amount(value)}</dd>
                    </div>
                  ))}
                  <div className={`${styles.row} ${styles.result}`}>
                    <dt>Result</dt>
                    <dd>{amount(summary.profitability.result)}</dd>
                  </div>
                  <div className={styles.row}>
                    <dt>Real GGR</dt>
                    <dd>
                      {realGgrReady
                        ? amount(
                            summary.profitability.realGgr!,
                            summary.profitability.realGgrCurrency,
                          )
                        : "Unavailable"}
                    </dd>
                  </div>
                </dl>
              </div>
            }
          >
            <button
              type="button"
              className={`${styles.trigger} ${styles[status!]}`}
              aria-expanded={open}
              aria-controls={open ? id : undefined}
              aria-haspopup="dialog"
              onClick={() => setOpen(!open)}
            >
              <span className={styles.pill}>{labels[status!]}</span>
              <span className={styles.amount}>
                {amount(summary.profitability.result)}
              </span>
            </button>
          </Tippy>
          <Tooltip text="Click to view profitability details" position="top">
            <span className="info-icon">ℹ</span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
